import { useState } from 'react';
import { EnvelopeSimple, Lightning, LinkSimple, Phone, Plus, Star, Trash, Wrench } from '@phosphor-icons/react';
import { BestaetigenDialog, Chip, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand, SearchSelect, ToastPill, useToastPill, type SucheOption } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { bereinigeSuchbegriff } from '../../../../../lib/kontakte/personenSuche';
import { entferneObjektLink, setzeObjektLink } from '../../../../../lib/dienstleister/api';
import type { DienstleisterZeile } from '../../../../../lib/immobilien/useObjektDetail';
import { useObjektSeite } from '../ObjektSeitenKontext';

/** Suche in aktiven Dienstleistern – gleiche Abfrage wie bisher, höchstens 8 Treffer. */
async function sucheDienstleister(anfrage: string, _typ: string | null, signal: AbortSignal): Promise<SucheOption[]> {
  const q = bereinigeSuchbegriff(anfrage);
  let abfrage = supabase.from('dienstleister').select('id, name, ansprechpartner, email').eq('aktiv', true).limit(8);
  if (q) abfrage = abfrage.or(`name.ilike.%${q}%,ansprechpartner.ilike.%${q}%,email.ilike.%${q}%`);
  const { data, error } = await abfrage.order('name').abortSignal(signal);
  if (error) throw new Error(error.message);
  return ((data ?? []) as { id: string; name: string; ansprechpartner: string | null; email: string | null }[])
    .map(d => ({ id: d.id, label: d.name, sub: [d.ansprechpartner, d.email].filter(Boolean).join(' · ') || undefined }));
}

/** Panel „Dienstleister“: zugeordnete Dienstleister mit Kategorien, Notfall/bevorzugt; zuordnen und entfernen. */
export function DienstleisterPanel() {
  const { daten, neuLaden } = useObjektSeite();
  const openKontakt = useOpenKontakt();
  const [toast, zeige] = useToastPill();
  const [gewaehlt, setGewaehlt] = useState<SucheOption | null>(null);
  const [vertragsart, setVertragsart] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [entfernen, setEntfernen] = useState<DienstleisterZeile | null>(null);
  const objektId = daten.objekt.id;
  const liste = [...daten.dienstleister].sort((a, b) => Number(b.ist_notfallkontakt) - Number(a.ist_notfallkontakt) || Number(b.ist_bevorzugt) - Number(a.ist_bevorzugt) || a.name.localeCompare(b.name, 'de'));

  const zuordnen = async () => {
    if (!gewaehlt) return;
    if (daten.dienstleister.some(d => d.id === gewaehlt.id)) { zeige(`${gewaehlt.label} ist bereits zugeordnet`, true); return; }
    setLaeuft(true);
    try {
      await setzeObjektLink(gewaehlt.id, objektId, null, null, vertragsart.trim() || null);
      zeige(`${gewaehlt.label} zugeordnet`);
      setGewaehlt(null); setVertragsart('');
      neuLaden('dienstleister');
    } catch (e) {
      zeige(`Nicht zugeordnet: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  const bestaetigeEntfernen = async () => {
    if (!entfernen) return;
    setLaeuft(true);
    try {
      await entferneObjektLink(entfernen.id, objektId);
      zeige(`${entfernen.name} entfernt`);
      setEntfernen(null);
      neuLaden('dienstleister');
    } catch (e) {
      zeige(`Nicht entfernt: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {liste.length === 0 ? (
        <LeerZustand icon={Wrench} titel="Keine Dienstleister zugeordnet" text="Ordne Handwerker, Hausmeister oder Wartungsfirmen zu – sie erscheinen dann bei Tickets und Aufträgen." />
      ) : (
        <>
          <KpiReihe>
            <KpiTile label="Zugeordnet" wert={liste.length} sub="Dienstleister" icon={Wrench} ton="acc" />
            <KpiTile label="Notfallkontakte" wert={liste.filter(d => d.ist_notfallkontakt).length} sub="für Notfälle" icon={Lightning} ton="bad" />
            <KpiTile label="Bevorzugt" wert={liste.filter(d => d.ist_bevorzugt).length} sub="werden zuerst vorgeschlagen" icon={Star} ton="warn" />
          </KpiReihe>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 10 }}>
            {liste.map(d => (
              <div key={d.id} className="immo-innen flex flex-col" style={{ borderRadius: 20, padding: 14, gap: 10 }}>
                <div className="flex items-center" style={{ gap: 12 }}>
                  <span className="flex items-center justify-center shrink-0" style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--acc-soft)' }}><Wrench weight="fill" size={20} color="var(--acc)" /></span>
                  <button type="button" onClick={() => openKontakt(d.id, 'dienstleister')} className="flex flex-col flex-1 min-w-0 text-left cursor-pointer" style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)' }}>
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{d.name}</span>
                    <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{d.kategorien.map(k => k.name).join(', ') || 'Ohne Kategorie'}</span>
                  </button>
                  <button type="button" aria-label={`${d.name} vom Objekt entfernen`} title="Zuordnung entfernen" onClick={() => setEntfernen(d)}
                    className="flex items-center justify-center cursor-pointer" style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
                    <Trash size={16} />
                  </button>
                </div>
                <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
                  {d.ist_notfallkontakt && <Chip ton="bad" groesse="xs" icon={Lightning}>Notfall</Chip>}
                  {d.ist_bevorzugt && <Chip ton="warn" groesse="xs" icon={Star}>Bevorzugt</Chip>}
                  {d.ansprechpartner && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink2)' }}>{d.ansprechpartner}</span>}
                  <span className="flex-1" />
                  {d.telefon && <a href={`tel:${d.telefon.replace(/\s+/g, '')}`} aria-label={`${d.name} anrufen`} className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--ok-soft)', color: 'var(--ok)' }}><Phone weight="fill" size={15} /></a>}
                  {d.email && <a href={`mailto:${d.email}`} aria-label={`E-Mail an ${d.name}`} className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--acc-soft)', color: 'var(--acc)' }}><EnvelopeSimple weight="fill" size={15} /></a>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <InnenKarte>
        <KartenTitel icon={LinkSimple}>Dienstleister zuordnen</KartenTitel>
        <div className="flex flex-wrap items-end" style={{ gap: 10 }}>
          <div style={{ flex: '2 1 260px' }}>
            <SearchSelect label="Dienstleister" platzhalter="Firma, Ansprechpartner oder E-Mail …" suche={sucheDienstleister} onWahl={setGewaehlt} ausgewaehlt={gewaehlt} onEntfernen={() => setGewaehlt(null)} />
          </div>
          <div className="flex flex-col" style={{ flex: '1 1 180px', gap: 6 }}>
            <label htmlFor="dl-vertragsart" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Vertragsart</label>
            <input id="dl-vertragsart" value={vertragsart} onChange={e => setVertragsart(e.target.value)} placeholder="z. B. Wartungsvertrag"
              style={{ height: 50, borderRadius: 15, padding: '0 14px', border: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }} />
          </div>
          <ImmoButton art="akzent" groesse={48} pille={false} icon={Plus} disabled={!gewaehlt || laeuft} onClick={() => { void zuordnen(); }}>Zuordnen</ImmoButton>
        </div>
      </InnenKarte>

      {entfernen && (
        <BestaetigenDialog titel="Dienstleister entfernen?" text="Die Zuordnung zum Objekt wird entfernt, der Dienstleister selbst bleibt erhalten." detail={entfernen.name}
          bestaetigenLabel="Entfernen" laeuft={laeuft} onBestaetigen={() => { void bestaetigeEntfernen(); }} onAbbrechen={() => setEntfernen(null)} />
      )}
    </>
  );
}
